// The string catalog.
//
// Every word a user reads used to be a literal at the point it was rendered,
// which is fine until somebody who does not read English arrives -- and Lumi
// has pages for exactly those people (/new-in-a-country, /practice-a-language).
//
// Three rules this exists to keep:
//
//   ENGLISH IS THE SOURCE. Keys are not abstract identifiers; the English
//   string IS the key. A missing translation renders the English rather than
//   `chat.controls.skip.label`, so the worst failure is an untranslated word,
//   never a broken-looking screen. It also means adding a string costs nothing
//   until somebody wants it translated.
//
//   INTERPOLATION IS NAMED. `t('Say hi to {name}!', { name })`, never
//   positional -- word order changes between languages, so a translator has to
//   be able to move the placeholder.
//
//   DIRECTION IS A PROPERTY OF THE LANGUAGE, not of a stylesheet. Choosing a
//   right-to-left language sets `dir` on the document, and the CSS follows with
//   logical properties. See applyLanguage().
//
// Deliberately tiny: no library, no build step, no plural-form machinery until
// something needs one. ASCII only in this file; the catalogs are not ASCII, and
// cannot be.
// ===========================================================================

import { CATALOGS, LANGUAGES, RTL_LANGUAGES, offeredLanguages } from './locales/index.js';

const STORAGE_KEY = 'lumi.uiLang';

/** The language actually in use. English until chooseLanguage says otherwise. */
let current = 'en';
let table = {};

/** What a picker may show: reviewed catalogs only. See locales/index.js. */
export { offeredLanguages };

export function isRtl(tag = current) {
  return RTL_LANGUAGES.has(primary(tag));
}

/** 'en-GB' -> 'en'. The catalogs are keyed by primary subtag. */
function primary(tag) {
  return String(tag || '').trim().split(/[-_]/)[0].toLowerCase();
}

/**
 * The language to start in: what they chose last time, else what the browser
 * asks for, else English. A browser asking for a language with no catalog gets
 * English rather than a half-translated screen.
 */
export function detectLanguage(nav = globalThis.navigator, store = globalThis.localStorage, search = globalThis.location?.search) {
  // ?lang=xx loads a catalog DELIBERATELY, reviewed or not: that is how an
  // unreviewed one gets read and corrected. It sticks, so the reviewer can
  // walk the whole site.
  try {
    const asked = primary(new URLSearchParams(search || '').get('lang'));
    if (asked && LANGUAGES[asked]) return asked;
  } catch {
    /* a malformed query string is not worth failing over */
  }
  try {
    const saved = primary(store?.getItem(STORAGE_KEY));
    if (saved && LANGUAGES[saved]) return saved;
  } catch {
    /* storage is a convenience */
  }
  // The browser's preference may only select a REVIEWED catalog: nobody should
  // be given a machine-guessed translation of "report" because of a header.
  const wanted = [nav?.language, ...(nav?.languages || [])];
  for (const tag of wanted) {
    const p = primary(tag);
    if (p && LANGUAGES[p]?.reviewed) return p;
  }
  return 'en';
}

/**
 * Switch language: load the catalog, remember the choice, and set `lang` and
 * `dir` on the document so CSS and screen readers both follow.
 * @param {string} tag
 * @param {Document} [doc]
 */
export function applyLanguage(tag, doc = globalThis.document) {
  const p = primary(tag);
  current = LANGUAGES[p] ? p : 'en';
  table = CATALOGS[current] || {};
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, current);
  } catch {
    /* non-fatal */
  }
  const root = doc?.documentElement;
  if (root) {
    root.setAttribute('lang', current);
    root.setAttribute('dir', isRtl(current) ? 'rtl' : 'ltr');
  }
  return current;
}

export function currentLanguage() {
  return current;
}

/**
 * Translate. The English string is the key and the fallback.
 *
 * @param {string} english  the source string, with {named} placeholders
 * @param {Record<string, string|number>} [vars]
 * @returns {string}
 */
export function t(english, vars) {
  const s = (typeof english === 'string' && table[english]) || english || '';
  if (!vars) return s;
  return String(s).replace(/\{(\w+)\}/g, (whole, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole,
  );
}

export default { t, applyLanguage, detectLanguage, currentLanguage, offeredLanguages, isRtl };
